'use client'

import { useEffect, useRef } from 'react'
import Script from 'next/script'

declare global {
  interface Window {
    /** Set by Clutch's widget script once it has loaded. */
    CLUTCHCO?: { Init?: () => void }
  }
}

/** Kirana Labs' Clutch profile. */
const CLUTCH_COMPANY_ID = '1762554'

/**
 * After the dark-mode inversion, maps black to the dark footer surface
 * (#1b1815) and white to the dark text colour (#ede8e2), channel by channel:
 * out = low + (high - low) × in. Used by globals.css as url(#kl-clutch-dark).
 */
const DARK_TONE =
  '0.8235 0 0 0 0.1059  0 0.8157 0 0 0.0941  0 0 0.8039 0 0.0824  0 0 0 1 0'

/**
 * Clutch's official review badge (their type-14 embed, unchanged). It is an
 * iframe from widget.clutch.co, so site CSS can't restyle its content; in
 * dark mode globals.css keeps its background transparent (the frame is
 * declared light, matching its document) and inverts it, then tone-maps it
 * with DARK_TONE so its card takes the footer's surface and its text the
 * site's text colour. Clutch's own dark option drew a box. The script loads
 * lazily after the page is idle and renders every `.clutch-widget` it finds
 * once. The footer remounts on client-side navigation, so a later mount
 * asks the already-loaded script to render the new container. Next keeps
 * visited pages mounted but hidden and re-runs their effects when they show
 * again, so only a still-empty container triggers a render.
 */
export function ClutchWidget() {
  const container = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (container.current && !container.current.hasChildNodes()) window.CLUTCHCO?.Init?.()
  }, [])
  return (
    <>
      <div
        ref={container}
        className="clutch-widget home-footer-clutch"
        data-url="https://widget.clutch.co"
        data-widget-type="14"
        data-height="50"
        data-nofollow="false"
        data-expandifr="true"
        data-scale="100"
        data-clutchcompany-id={CLUTCH_COMPANY_ID}
      />
      <svg aria-hidden="true" focusable="false" width="0" height="0" className="home-footer-clutch-filter">
        <filter id="kl-clutch-dark" colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values={DARK_TONE} />
        </filter>
      </svg>
      <Script src="https://widget.clutch.co/static/js/widget.js" strategy="lazyOnload" />
    </>
  )
}
